import { useState } from 'react';
import { Knop } from '@qc/ui';
import { useDemodata } from './useDemodata.js';

export interface DemomodusProps {
  onKlaar: () => Promise<void>;
}

/**
 * De demomodus. Eén knop vult een compleet nepevenement met kramen, menu's en
 * een dag omzet die realistisch verloopt; één knop wist alles weer.
 *
 * De wisknop kan onmogelijk echte data raken: hij roept een functie in de
 * database aan die alleen naar evenementen met de demovlag kijkt, en die stopt
 * zodra er één rij tussen zit die geen demodata is. Bovendien kunnen demo en
 * echt niet aan elkaar geknoopt worden — dat weigert de database al bij het
 * opslaan.
 */
export function Demomodus({ onKlaar }: DemomodusProps) {
  const demo = useDemodata(onKlaar);
  const [bevestigen, setBevestigen] = useState(false);

  return (
    <section className="mb-8 rounded-tegel border border-rand bg-vlak p-6">
      <h2 className="mb-1 text-xl font-bold">Demomodus</h2>
      <p className="mb-4 text-tekst-zacht">
        Een compleet nepevenement om in twee minuten te laten zien hoe het werkt. De bonnen die deze
        knop aanmaakt zijn nep; daarvoor is niets afgeschreven. Pin je daarna zelf op een kassa die
        op een demokraam is ingelogd, dan is dat wél een echte pinbetaling via Rabo SmartPin.
      </p>

      {demo.stap !== null && <p className="mb-3 text-lg text-tekst-zacht">{demo.stap}</p>}
      {demo.fout !== null && <p className="mb-3 text-lg text-fout">{demo.fout}</p>}

      {demo.gevuld !== null && (
        <div className="mb-4 rounded-tegel border border-accent bg-vlak-hoog p-4">
          <p className="mb-2 text-lg">
            Klaar: {demo.gevuld.aantalKramen} kramen en {demo.gevuld.aantalBonnen} bonnen.
          </p>
          <p className="mb-2 text-tekst-zacht">Inloggen op de kassa met een van deze codes:</p>
          <ul className="grid gap-1">
            {demo.gevuld.inlogcodes.map((regel) => (
              <li key={regel.code} className="tabular-nums">
                <span className="text-tekst-zacht">{regel.kraam}: </span>
                <span className="font-bold tracking-wider text-accent">{regel.code}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-base text-tekst-zacht">
            De eerste twee codes hebben al een kassa-inlog. Voor de andere kramen moet er nog een
            account worden aangemaakt.
          </p>
        </div>
      )}

      {demo.gewist !== null && (
        <p className="mb-4 text-lg text-goed">
          Gewist: {demo.gewist.evenementen}{' '}
          {demo.gewist.evenementen === 1 ? 'evenement' : 'evenementen'} en {demo.gewist.bonnen}{' '}
          bonnen. Echte data is niet aangeraakt.
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <Knop nadruk="hoofd" disabled={demo.bezig} onClick={() => void demo.vulDemodata()}>
          {demo.bezig ? 'Bezig…' : 'Demodata vullen'}
        </Knop>

        {!bevestigen ? (
          <Knop nadruk="gewoon" disabled={demo.bezig} onClick={() => setBevestigen(true)}>
            Demodata wissen
          </Knop>
        ) : (
          <>
            <Knop
              nadruk="gevaar"
              disabled={demo.bezig}
              onClick={() => {
                setBevestigen(false);
                void demo.wisDemodata();
              }}
            >
              Ja, alle demodata wissen
            </Knop>
            <Knop nadruk="stil" onClick={() => setBevestigen(false)}>
              Nee, laat staan
            </Knop>
          </>
        )}
      </div>
    </section>
  );
}
